import AddressInput from '@/components/AddressFormFields/AddressInput';
import { HoverOnlyTooltipComponent } from '@/components/Tooltip';
import { isErrorWithData } from '@/services/helpers';
import { usePutOrganizationCoverageAreaSettingsMutation } from '@/services/organizations';
import { Box, Button, Card, TextInput, Toggle, toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray, Controller, UseFormSetValue, UseFormRegister } from 'react-hook-form';
import { geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';

import {
  CoverageAreaEstimates,
  CoverageAreaValue,
  CoverageRegionType,
  OrganizationCoverageAreaSettingsDocument,
  ProgramType,
  RadiusCoverageRegion,
} from 'types';

import { ForecastVolumeTable } from './ForecastVolumeTable';
import { getStateName } from './stateNames';

const noop = () => {};
const preventSubmit = (e: React.FormEvent) => e.preventDefault();
const TOOLTIP_CONTENT_STYLE = { width: '260px' };
const EXCEED_LABEL_STYLE: React.CSSProperties = {
  color: 'var(--color-text-body-secondary)',
  cursor: 'default',
  textDecoration: 'underline dotted',
  textUnderlineOffset: '3px',
  textDecorationColor: 'currentColor',
};
const HELPER_TEXT_STYLE: React.CSSProperties = { color: 'var(--color-text-body-secondary)' };

interface CapacityStateCardProps {
  alias: string;
  state: string;
  programType: ProgramType;
  setting: OrganizationCoverageAreaSettingsDocument | undefined;
}

interface FormValues {
  allowExceedForecast: boolean;
  minPricePerWatt: string;
  regions: { address: string; radius: string; lat: number; lon: number }[];
  estimates: CoverageAreaEstimates;
}

function buildDefaultValues(setting: OrganizationCoverageAreaSettingsDocument | undefined): FormValues {
  return {
    allowExceedForecast: setting?.value?.allowExceedForecast ?? false,
    minPricePerWatt: setting?.value?.minPricePerWatt != null ? String(setting.value.minPricePerWatt) : '',
    regions: setting?.value?.regions?.length
      ? setting.value.regions
          .filter((r): r is RadiusCoverageRegion => r.type === CoverageRegionType.radius)
          .map((r) => ({
            address: r.address,
            radius: r.radius != null ? String(r.radius) : '',
            lat: r.coordinates?.lat ?? 0,
            lon: r.coordinates?.lon ?? 0,
          }))
      : [{ address: '', radius: '', lat: 0, lon: 0 }],
    estimates: setting?.value?.estimates ?? {},
  };
}

interface RegionRowProps {
  fieldId: string;
  index: number;
  state: string;
  programType: ProgramType;
  addressValue: string;
  radiusValue: string;
  isSingle: boolean;
  isLast: boolean;
  setValue: UseFormSetValue<FormValues>;
  register: UseFormRegister<FormValues>;
  remove: (index: number) => void;
  onAdd: () => void;
}

function RegionRow({
  fieldId,
  index,
  state,
  programType,
  addressValue,
  radiusValue,
  isSingle,
  isLast,
  setValue,
  register,
  remove,
  onAdd,
}: RegionRowProps) {
  const handleAddressChange = useCallback(
    (value: string) => setValue(`regions.${index}.address`, value),
    [setValue, index],
  );
  const handleAddressSelect = useCallback(
    async (address: string, placeId: string) => {
      setValue(`regions.${index}.address`, address);
      try {
        const [result] = await geocodeByPlaceId(placeId);
        setValue(`regions.${index}.address`, result.formatted_address);
        const { lat, lng } = await getLatLng(result);
        setValue(`regions.${index}.lat`, lat);
        setValue(`regions.${index}.lon`, lng);
      } catch {
        setValue(`regions.${index}.lat`, 0);
        setValue(`regions.${index}.lon`, 0);
      }
    },
    [setValue, index],
  );
  const handleRemove = useCallback(() => remove(index), [remove, index]);

  const { onChange: onRadiusChange, ...radiusRegister } = register(`regions.${index}.radius`, {
    required: true,
    min: 0,
  });

  const handleRadiusChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (parseFloat(e.target.value) < 0) e.target.value = '0';
      onRadiusChange(e);
    },
    [onRadiusChange],
  );

  return (
    <Box key={fieldId} direction="row" childGap="sm" alignItems="flex-end">
      <Box flex="auto">
        <AddressInput
          id={`region-address-${state}-${programType}-${index}`}
          label="Operating Address"
          name={`regions.${index}.address`}
          value={addressValue}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
        />
      </Box>
      <Box className="w-[120px]">
        <TextInput
          id={`region-radius-${state}-${programType}-${index}`}
          label="Radius from address"
          suffix="miles"
          type="number"
          min={0}
          value={radiusValue}
          onChange={handleRadiusChange}
          {...radiusRegister}
        />
      </Box>
      <Box direction="row" childGap="xs" justifyContent="space-between" className="w-[120px]">
        {isSingle ? (
          <Button type="button" variant="secondary" iconPrefix="add" onClick={onAdd} fullWidth>
            Address
          </Button>
        ) : isLast ? (
          <>
            <Button
              type="button"
              variant="secondary"
              tone="danger"
              iconPrefix="trash"
              onClick={handleRemove}
              aria-label={`Remove region ${index + 1}`}
            />
            <Button type="button" variant="secondary" iconPrefix="add" onClick={onAdd} aria-label="Add address" />
          </>
        ) : (
          <Button
            type="button"
            variant="secondary"
            tone="danger"
            iconPrefix="trash"
            onClick={handleRemove}
            fullWidth
          >
            Delete
          </Button>
        )}
      </Box>
    </Box>
  );
}

export function CapacityStateCard({ alias, state, programType, setting }: CapacityStateCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [putCoverageAreaSettings, { isLoading: isSaving }] = usePutOrganizationCoverageAreaSettingsMutation();

  const { handleSubmit, control, reset, setValue, watch, register } = useForm<FormValues>({
    defaultValues: buildDefaultValues(setting),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'regions' });

  const watchedEstimates = watch('estimates');

  useEffect(() => {
    reset(buildDefaultValues(setting));
  }, [setting, reset]);

  const handleCancel = useCallback(() => {
    reset(buildDefaultValues(setting));
    setIsEditing(false);
  }, [setting, reset]);

  const handleStartEditing = useCallback(() => setIsEditing(true), []);

  const handleEstimatesChange = useCallback(
    (estimates: CoverageAreaEstimates) => {
      setValue('estimates', estimates);
    },
    [setValue],
  );

  const handleAddRegion = useCallback(() => {
    append({ address: '', radius: '', lat: 0, lon: 0 });
  }, [append]);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const regions = data.regions.map((r) => ({
        type: CoverageRegionType.radius,
        address: r.address,
        coordinates: { lat: r.lat, lon: r.lon },
        radius: parseFloat(r.radius) || 0,
      }));

      const value: Omit<CoverageAreaValue, 'setBy' | 'history'> = {
        regions,
        estimates: data.estimates,
        allowExceedForecast: data.allowExceedForecast,
        minPricePerWatt: data.minPricePerWatt !== '' ? parseFloat(data.minPricePerWatt) : 0,
      };

      try {
        await putCoverageAreaSettings({ alias, state, programType, value }).unwrap();
        toast.success('Coverage area settings saved successfully');
        setIsEditing(false);
      } catch (error) {
        if (isErrorWithData(error)) {
          toast.error(error.data.message ?? 'Error saving settings');
        } else {
          toast.error('Error saving settings');
        }
      }
    },
    [alias, state, programType, setting, putCoverageAreaSettings],
  );

  const stateName = getStateName(state);
  const cardTitle = `${stateName} (${state})`;

  const allowExceedForecastValue = watch('allowExceedForecast');
  const minPricePerWattValue = watch('minPricePerWatt');
  const regionsValue = watch('regions');

  const statusStyle = useMemo(
    () => ({
      color: allowExceedForecastValue ? 'var(--color-text-success)' : 'var(--color-text-body-secondary)',
      fontWeight: 500,
    }),
    [allowExceedForecastValue],
  );

  const tooltipContent = allowExceedForecastValue
    ? 'Keep receiving leads above your forecast volume.'
    : "You won't receive leads once you reach forecast volume.";

  return (
    <Card padding="lg">
      <form onSubmit={preventSubmit}>
        <Box direction="row" justifyContent="space-between" alignItems="center" padding="0 0 md 0">
          <Box direction="row" alignItems="center" childGap="md">
            <span className="text-xl font-semibold">{cardTitle}</span>
            <Box direction="row" alignItems="center" childGap="xs">
              {isEditing && (
                <Controller
                  control={control}
                  name="allowExceedForecast"
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                  render={({ field }) => (
                    <Toggle
                      id={`exceed-forecast-${state}-${programType}`}
                      label="Exceed forecast volume"
                      hideLabel
                      isChecked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
              <HoverOnlyTooltipComponent
                content={tooltipContent}
                onClose={noop}
                contentStyle={TOOLTIP_CONTENT_STYLE}
              >
                <span className="text-sm" style={EXCEED_LABEL_STYLE}>
                  Exceed forecast volume:&nbsp;
                </span>
              </HoverOnlyTooltipComponent>
              <span className="text-sm" style={statusStyle}>
                {allowExceedForecastValue ? 'Active' : 'Inactive'}
              </span>
            </Box>
          </Box>
          <Box direction="row" childGap="lg">
            {isEditing && (
              <Button type="button" variant="secondary" onClick={handleCancel} isDisabled={isSaving}>
                Cancel
              </Button>
            )}
            {isEditing ? (
              <Button
                type="button"
                variant="primary"
                isLoading={isSaving}
                isDisabled={regionsValue.length === 0}
                onClick={handleSubmit(onSubmit)}
              >
                Save Changes
              </Button>
            ) : (
              <Button
                type="button"
                variant="tertiary"
                tone="neutral"
                iconPrefix="pencil"
                onClick={handleStartEditing}
              >
                Edit
              </Button>
            )}
          </Box>
        </Box>

        {isEditing ? (
          <Box padding="sm 0" className="w-[200px]">
            <TextInput
              id={`min-price-${state}-${programType}`}
              label="Minimum price/Watt"
              prefix="$"
              suffix="/watt"
              type="number"
              value={minPricePerWattValue}
              {...register('minPricePerWatt')}
            />
          </Box>
        ) : (
          minPricePerWattValue && (
            <Box padding="sm 0">
              <span className="text-sm">Minimum price/Watt: ${parseFloat(minPricePerWattValue).toFixed(2)}</span>
            </Box>
          )
        )}

        {isEditing ? (
          <Box padding="md" background="secondary" radius="md" childGap="sm" margin="sm 0">
            {fields.map((field, index) => (
              <RegionRow
                key={field.id}
                fieldId={field.id}
                index={index}
                state={state}
                programType={programType}
                addressValue={regionsValue[index]?.address ?? ''}
                radiusValue={regionsValue[index]?.radius ?? ''}
                isSingle={fields.length === 1}
                isLast={index === fields.length - 1}
                setValue={setValue}
                register={register}
                remove={remove}
                onAdd={handleAddRegion}
              />
            ))}
            <Box>
              <span className="text-xs" style={HELPER_TEXT_STYLE}>
                Operating address and radius setting help us optimize leads for your business.
              </span>
            </Box>
          </Box>
        ) : (
          setting?.value?.regions
            ?.filter((r) => r.type === CoverageRegionType.radius)
            .map((region) => (
              <Box
                key={`${region.address}-${region.radius}`}
                direction="row"
                alignItems="center"
                childGap="sm"
                padding="xs 0"
              >
                <span aria-hidden="true">📍</span>
                <span className="text-sm">{region.address}</span>
                <span aria-hidden="true">↔</span>
                <span className="text-sm">Within {region.radius} miles radius</span>
              </Box>
            ))
        )}

        <Box margin="md 0 0 0">
          <ForecastVolumeTable estimates={watchedEstimates} isEditing={isEditing} onChange={handleEstimatesChange} />
        </Box>
      </form>
    </Card>
  );
}
